// src/components/album/LiveCommentsStream.jsx
import React, { useState, useEffect, useRef } from 'react';
import { User, Heart, Reply, MessageCircle, MoreHorizontal } from 'lucide-react';

const CommentBubble = ({ comment, isOwn = false }) => {
  const formatTime = (timestamp) => {
    const now = new Date();
    const commentTime = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInMinutes = Math.floor((now - commentTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  return (
    <div className={`flex items-start gap-3 py-2 px-3 ${isOwn ? 'bg-blue-50' : ''}`}>
      <div className="flex-shrink-0">
        {comment.userAvatar ? (
          <img 
            src={comment.userAvatar} 
            alt={comment.userName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
            <User className="h-4 w-4 text-gray-600" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm text-gray-900">
            {comment.userName || 'Anonymous'}
          </span>
          <span className="text-xs text-gray-500">
            {formatTime(comment.timestamp)}
          </span>
        </div>
        
        <p className="text-sm text-gray-800 break-words">
          {comment.content}
        </p>
        
        <div className="flex items-center gap-4 mt-2">
          <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors">
            <Heart className="h-3 w-3" />
            <span>{comment.likes || 0}</span>
          </button>
          
          <button className="text-xs text-gray-500 hover:text-gray-700 transition-colors">
            <Reply className="h-3 w-3 inline mr-1" />
            Reply
          </button>
        </div>
      </div>
      
      <button className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors">
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </div>
  );
};

const LiveCommentsStream = ({ 
  comments = [], 
  isVisible = true, 
  currentUserId,
  onToggleVisibility 
}) => {
  const [newCommentsCount, setNewCommentsCount] = useState(0);
  const commentsEndRef = useRef(null);
  const lastReadRef = useRef(comments.length);

  // Auto-scroll to bottom when new comments arrive
  useEffect(() => {
    if (isVisible && commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Count new comments when stream is hidden
    if (!isVisible && comments.length > lastReadRef.current) {
      setNewCommentsCount(comments.length - lastReadRef.current);
    }
  }, [comments.length, isVisible]);

  // Reset new comments count when stream becomes visible
  useEffect(() => {
    if (isVisible) {
      setNewCommentsCount(0);
      lastReadRef.current = comments.length;
    }
  }, [isVisible, comments.length]);

  if (!isVisible) {
    return (
      <div className="absolute bottom-4 left-48">
        <button
          onClick={onToggleVisibility}
          className="flex items-center gap-2 bg-black bg-opacity-50 text-white px-3 py-2 rounded-full backdrop-blur-sm hover:bg-opacity-70 transition-colors"
        >
          <MessageCircle className="h-4 w-4" />
          <span className="text-sm">
            {comments.length > 0 ? `${comments.length} comments` : 'Comments'}
          </span>
          {newCommentsCount > 0 && (
            <div className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {newCommentsCount}
            </div>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="absolute bottom-0 left-0 right-0 max-h-80 bg-white bg-opacity-20 backdrop-blur-sm border-t border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-gray-600" />
          <span className="font-semibold text-gray-900">
            Comments ({comments.length})
          </span>
        </div>
        
        <button
          onClick={onToggleVisibility}
          className="text-white hover:text-gray-700 transition-colors"
        >
          ✕
        </button>
      </div>
      
      {/* Comments List */}
      <div className="max-h-60 overflow-y-auto">
        {comments.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No comments yet</p>
            <p className="text-xs">Be the first to comment!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {comments.map((comment) => (
              <CommentBubble
                key={comment.id}
                comment={comment}
                isOwn={comment.userId === currentUserId}
              />
            ))}
            <div ref={commentsEndRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveCommentsStream;